<template>
  <div class="content trace-detail-new" v-loading="loading">
    <div class="detail-header">
      <div class="detail-heading">
        <div class="pageTitle detail-title">追踪稽查详情（新版）</div>
        <div class="current-code">物流码：{{ displayValue(form.logistics_code) }}</div>
      </div>
      <el-button icon="el-icon-back" @click="backToSearch">返回查询</el-button>
    </div>

    <div v-if="isTrue" class="detail-body">
      <el-tabs
        v-model="activeSection"
        type="card"
        class="detail-tabs"
        @tab-click="handleTabClick"
      >
        <el-tab-pane label="基本信息" name="baseData">
          <section class="section-panel">
            <div class="panel-title">基本信息</div>
            <div class="info-grid">
              <div class="info-item">
                <div class="info-label">状态</div>
                <div class="info-value">
                  <span class="status-tag" :style="statusTagStyle">
                    {{ baseStatusText }}
                  </span>
                </div>
              </div>
              <div
                v-for="field in baseFields"
                :key="field.key"
                class="info-item"
              >
                <div class="info-label">{{ field.label }}</div>
                <div class="info-value">
                  {{ displayValue(baseData[field.key]) }}
                </div>
              </div>
              <div v-if="showCodeSellsRate" class="info-item">
                <div class="info-label">开瓶率</div>
                <div class="info-value">{{ withCodeInfo.codeSellsRate }}%</div>
              </div>
            </div>
          </section>
        </el-tab-pane>

        <el-tab-pane label="物流追溯" name="deliverData">
          <section class="section-panel">
            <div class="panel-title">物流追溯</div>
            <div v-if="deliverRecords.length" class="record-list">
              <article
                v-for="record in deliverRecords"
                :key="record.key"
                class="record-card"
              >
                <header class="record-header">
                  <div class="record-identity">
                    <span
                      class="record-status"
                      :style="getRecordStatusStyle(record.meta.status)"
                    >
                      {{ record.meta.statusLabel }}
                    </span>
                    <span class="record-order">
                      单号：{{ displayValue(record.meta.orderNo) }}
                    </span>
                  </div>
                  <time class="record-time">
                    {{ displayValue(record.item.created_at) }}
                  </time>
                </header>
                <div class="record-grid">
                  <div
                    v-for="row in record.rows"
                    :key="row.key"
                    class="record-field"
                  >
                    <div class="record-label">{{ row.label }}</div>
                    <div class="record-value">{{ displayValue(row.value) }}</div>
                  </div>
                </div>
              </article>
            </div>
            <div v-else class="empty-state">暂无物流追溯记录</div>
          </section>
        </el-tab-pane>

        <el-tab-pane label="物流码信息" name="codeData">
          <section class="section-panel">
            <div class="panel-title code-panel-title">
              <span>物流码信息</span>
              <div v-if="codeData.length" class="code-summary">
                <span>{{ bigCodeLabel }} {{ codeCounts[0] }}</span>
                <span>{{ middleCodeLabel }} {{ codeCounts[1] }}</span>
                <span>{{ smallCodeLabel }} {{ codeCounts[2] }}</span>
              </div>
            </div>
            <div v-if="codeData.length" class="code-tree-wrap">
              <el-tree
                :key="codeTreeKey"
                ref="codeTree"
                class="code-tree"
                :data="codeData"
                :props="treeProps"
                node-key="key"
                :default-expanded-keys="expandedCodeKeys"
                :current-node-key="currentCodeKey"
                :expand-on-click-node="true"
                highlight-current
              >
                <div
                  slot-scope="{ data }"
                  class="code-node"
                  :class="{ 'code-node-target': data.key === currentCodeKey }"
                  :data-node-key="data.key"
                >
                  <div class="code-node-main">
                    <span class="code-node-label">
                      {{ getCodeLevelLabel(data.level) }}
                    </span>
                    <span class="code-node-value">
                      {{ displayValue(data.code) }}
                    </span>
                  </div>
                  <div class="code-node-meta">
                    <el-tooltip
                      v-if="isSoldCode(data)"
                      :content="getSoldDate(data.code)"
                      placement="top"
                    >
                      <img
                        src="@/assets/img/sells.png"
                        alt="已销售"
                        class="sales-icon"
                      />
                    </el-tooltip>
                    <el-button
                      v-if="data.level === 0 && data.stack_code"
                      type="text"
                      size="mini"
                      class="stack-action"
                      @click.stop="showStack(data.code)"
                    >
                      垛标详情
                    </el-button>
                    <span v-if="data.children && data.children.length">
                      {{ getChildLevelLabel(data.level) }}
                      {{ data.children.length }}
                    </span>
                    <span v-else-if="data.serialnum">
                      流水号 {{ data.serialnum }}
                    </span>
                    <span v-if="data.updated_at">
                      变更时间 {{ data.updated_at }}
                    </span>
                  </div>
                </div>
              </el-tree>
            </div>
            <div v-else class="empty-state">暂无物流码信息</div>
          </section>
        </el-tab-pane>
      </el-tabs>
    </div>

    <stack-view
      :visible.sync="stackVisible"
      :logistics_code="stackLogisticsCode"
    />
  </div>
</template>

<script>
import StackView from "../../common/StackView.vue";
import {
  countCodeTree,
  filterLogisticsItems,
  findCodeTreePath,
  getBaseStatusText,
  getStatusMeta,
  normalizeCodeTree,
  normalizeFlowItem,
  normalizeLogisticsCode,
} from "./traceUtils";

export default {
  name: "trace-manage-detail-new",
  components: {
    StackView,
  },
  data() {
    return {
      form: {
        logistics_code: "",
      },
      activeSection: "baseData",
      baseData: {},
      codeData: [],
      deliverData: [],
      withCodeInfo: {
        codeSells: [],
        codeSellsRate: "",
        codeSellsDate: {},
      },
      bigCodeName: "大标",
      middleCodeName: "中标",
      smallCodeName: "小标",
      currentCodeKey: "",
      expandedCodeKeys: [],
      codeTreeKey: 0,
      treeProps: {
        children: "children",
        label: "code",
      },
      isTrue: false,
      loading: false,
      stackVisible: false,
      stackLogisticsCode: "",
    };
  },
  computed: {
    baseFields() {
      return [
        { key: "code_name", label: "标签类型" },
        { key: "production_batch", label: "批号" },
        { key: "packaging_type", label: "包装类型" },
        { key: "product_sn", label: "产品代码" },
        { key: "product_name", label: "产品名称" },
        { key: "product_specs", label: "产品规格" },
        { key: "code_total", label: "产品数量" },
        { key: "production_date", label: "生产日期" },
        { key: "expiring_date", label: "有效日期" },
      ];
    },
    baseStatusText() {
      return getBaseStatusText(this.baseData);
    },
    statusTagStyle() {
      const meta = getStatusMeta(this.baseData.status);
      return {
        color: meta.color,
        backgroundColor: meta.backgroundColor,
        borderColor: meta.color,
      };
    },
    showCodeSellsRate() {
      return (
        this.withCodeInfo.codeSellsRate !== "" &&
        this.withCodeInfo.codeSellsRate !== null &&
        this.withCodeInfo.codeSellsRate !== undefined
      );
    },
    deliverRecords() {
      return this.deliverData.map((item, index) => ({
        item,
        meta: normalizeFlowItem(item),
        rows: this.getDeliverRows(item),
        key: `${item.id || item.created_at || item.status || "record"}-${index}`,
      }));
    },
    codeCounts() {
      return countCodeTree(this.codeData);
    },
    bigCodeLabel() {
      return this.bigCodeName || "大标";
    },
    middleCodeLabel() {
      return this.middleCodeName || "中标";
    },
    smallCodeLabel() {
      return this.smallCodeName || "小标";
    },
  },
  watch: {
    "$route.query.logistics_code"(value) {
      const code = String(value || "").trim();
      if (!code) {
        this.resetDetail();
        this.$message({ type: "error", message: "缺少物流码" });
        return;
      }
      if (code !== this.form.logistics_code) {
        this.form.logistics_code = code;
        this.getDetail();
      }
    },
  },
  created() {
    this.form.logistics_code = String(
      this.$route.query.logistics_code || ""
    ).trim();
    this.getConfigInfo();
    if (this.form.logistics_code) {
      this.getDetail();
    } else {
      this.$message({ type: "error", message: "缺少物流码" });
    }
  },
  methods: {
    displayValue(value) {
      return value === null || value === undefined || value === ""
        ? "--"
        : value;
    },
    resetDetail() {
      this.baseData = {};
      this.codeData = [];
      this.deliverData = [];
      this.currentCodeKey = "";
      this.expandedCodeKeys = [];
      this.isTrue = false;
    },
    getConfigInfo() {
      this.$get("logistics", "/configs", { lang: "" })
        .then((res) => {
          const data = res.data || {};
          this.bigCodeName = data.big_code_name || this.bigCodeName;
          this.middleCodeName = data.middle_code_name || this.middleCodeName;
          this.smallCodeName = data.small_code_name || this.smallCodeName;
        })
        .catch(() => {});
    },
    getDetail() {
      const code = this.form.logistics_code;
      if (!code) {
        return;
      }

      this.loading = true;
      this.$get("logistics", "/logistics.search", {
        lang: "",
        logistics_code: code,
      })
        .then((res) => {
          const data = res.data || {};
          const withCodeInfo = data.with_code_info || {};
          const codeData = normalizeCodeTree(data.code_list);
          const codePath = findCodeTreePath(codeData, code);

          this.baseData = data.base_info || {};
          this.codeData = codeData;
          this.deliverData = filterLogisticsItems(
            data.logistics_info,
            data.config_info
          );
          this.withCodeInfo.codeSells = Array.isArray(withCodeInfo.code_sells)
            ? withCodeInfo.code_sells
            : [];
          this.withCodeInfo.codeSellsRate =
            withCodeInfo.code_sells_rate !== undefined
              ? withCodeInfo.code_sells_rate
              : "";
          this.withCodeInfo.codeSellsDate =
            withCodeInfo.code_sells_date || {};
          this.currentCodeKey = codePath.length
            ? codePath[codePath.length - 1]
            : "";
          this.expandedCodeKeys = codePath.slice(0, -1);
          this.codeTreeKey += 1;
          this.isTrue = true;
          this.loading = false;
          this.$nextTick(this.syncCodeTreeSelection);
        })
        .catch((error) => {
          this.resetDetail();
          this.loading = false;
          this.$message({
            type: "error",
            message: error.message,
          });
        });
    },
    getDeliverRows(item) {
      const status = Number(item.status);
      const rows = [];
      const addRow = (key, label, value) => {
        rows.push({ key, label, value });
      };

      if (status === 1) {
        addRow("warehouse", "入库仓库", item.warehouse_name);
        addRow("product", "入库产品", item.product_name);
      } else if (status === 2) {
        addRow("from_warehouse", "调出仓库", item.from_warehouse_name);
        addRow("warehouse", "调入仓库", item.warehouse_name);
        addRow("product", "入库产品", item.product_name);
      } else if (status === 3) {
        addRow("warehouse", "出货仓库", item.warehouse_name);
        addRow("from_name", "出库客户", item.from_name);
        addRow("to_name", "收货客户", item.to_name);
        addRow("product", "出库产品", item.product_name);
        if (item.order_line_no) {
          addRow("order_line_no", "在线订单单号", item.order_line_no);
        }
        if (item.isgift !== undefined && item.isgift !== null) {
          addRow(
            "isgift",
            "是否赠品",
            Number(item.isgift) === 1
              ? "赠品"
              : Number(item.isgift) === 2
              ? "非赠品"
              : "--"
          );
        }
      } else if (status === 4) {
        addRow("from_name", "调出经销商", item.from_name);
        addRow("to_name", "调入经销商", item.to_name);
        addRow("product", "入库产品", item.product_name);
      } else if (status === 5) {
        addRow("from_name", "出货人", item.from_name);
        addRow("to_name", "零售客户", item.to_name);
        addRow("product", "出货产品", item.product_name);
      } else if (status === 6 || status === 7) {
        addRow("from_name", "退货人", item.from_name);
        addRow("to_name", "退回商户", item.to_name);
        addRow("product", "退货产品", item.product_name);
      } else {
        addRow("warehouse", "仓库", item.warehouse_name);
        addRow("from_name", "来源", item.from_name);
        addRow("to_name", "去向", item.to_name);
        addRow("product", "产品", item.product_name);
      }

      if (item.remarks) {
        addRow("remarks", "备注", item.remarks);
      }
      addRow("operator", "操作人", item.operator);
      return rows;
    },
    getRecordStatusStyle(status) {
      const meta = getStatusMeta(status);
      return {
        color: meta.color,
        backgroundColor: meta.backgroundColor,
        borderColor: meta.color,
      };
    },
    getCodeLevelLabel(level) {
      return [this.bigCodeLabel, this.middleCodeLabel, this.smallCodeLabel][
        Number(level)
      ] || "物流码";
    },
    getChildLevelLabel(level) {
      const labels = [this.middleCodeLabel, this.smallCodeLabel, "下级"];
      return labels[Number(level)] || "下级";
    },
    isSoldCode(data) {
      if (!data || Number(data.level) !== 2) {
        return false;
      }
      const target = normalizeLogisticsCode(data.code);
      return this.withCodeInfo.codeSells.some(
        (code) => normalizeLogisticsCode(code) === target
      );
    },
    getSoldDate(code) {
      const dates = this.withCodeInfo.codeSellsDate || {};
      return dates[code] || "已销售";
    },
    handleTabClick(tab) {
      if (tab.name !== "codeData") {
        return;
      }
      this.$nextTick(() => {
        this.syncCodeTreeSelection();
        this.scrollToCurrentCode();
      });
    },
    syncCodeTreeSelection() {
      const tree = this.$refs.codeTree;
      if (!tree || !this.currentCodeKey) {
        return;
      }

      this.expandedCodeKeys.forEach((key) => {
        const node = tree.getNode(key);
        if (node) {
          node.expanded = true;
        }
      });
      tree.setCurrentKey(this.currentCodeKey);
    },
    scrollToCurrentCode() {
      if (!this.currentCodeKey) {
        return;
      }
      const elements = this.$el.querySelectorAll("[data-node-key]");
      const target = Array.prototype.slice.call(elements).find(
        (element) =>
          element.getAttribute("data-node-key") === this.currentCodeKey
      );
      if (!target || typeof target.scrollIntoView !== "function") {
        return;
      }
      try {
        target.scrollIntoView({ block: "center", behavior: "auto" });
      } catch (error) {
        target.scrollIntoView();
      }
    },
    showStack(bigCode) {
      this.stackLogisticsCode = bigCode;
      this.stackVisible = true;
    },
    backToSearch() {
      this.$router.replace({
        name: "traceManage",
        query: { logistics_code: this.form.logistics_code },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.trace-detail-new {
  min-height: 100%;
  background: #fff;

  .detail-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 20px;
    padding-right: 20px;
  }

  .detail-heading {
    min-width: 0;
  }

  .detail-title {
    margin-bottom: 2px;
  }

  .current-code {
    margin-left: 20px;
    color: #909399;
    font-family: monospace;
    font-size: 13px;
    line-height: 20px;
    overflow-wrap: anywhere;
  }

  .detail-body {
    margin: 18px 20px 30px;
  }

  .detail-tabs {
    /deep/ .el-tabs__header {
      margin-bottom: 16px;
    }

    /deep/ .el-tabs__item {
      min-width: 120px;
      color: #4887e7;
      text-align: center;
      background: #eef4ff;
      border-color: #b9d2f7;
    }

    /deep/ .el-tabs__item.is-active {
      color: #fff;
      background: #4887e7;
      border-color: #4887e7;
    }
  }

  .section-panel {
    width: 100%;
  }

  .panel-title {
    display: flex;
    min-height: 42px;
    align-items: center;
    justify-content: space-between;
    gap: 20px;
    padding: 0 16px;
    color: #303133;
    font-size: 16px;
    font-weight: 600;
    background: #f2f4f7;
    border-left: 4px solid #4887e7;
    box-sizing: border-box;
  }

  .info-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    margin-top: 12px;
    border-top: 1px solid #dcdfe6;
    border-left: 1px solid #dcdfe6;
  }

  .info-item {
    display: grid;
    grid-template-columns: 120px minmax(0, 1fr);
    min-height: 52px;
    border-right: 1px solid #dcdfe6;
    border-bottom: 1px solid #dcdfe6;
  }

  .info-label,
  .info-value {
    display: flex;
    min-width: 0;
    align-items: center;
    padding: 10px 14px;
    box-sizing: border-box;
  }

  .info-label {
    color: #606266;
    font-size: 13px;
    background: #fafafa;
    border-right: 1px solid #ebeef5;
  }

  .info-value {
    color: #303133;
    font-size: 14px;
    line-height: 22px;
    overflow-wrap: anywhere;
    word-break: break-all;
  }

  .status-tag,
  .record-status {
    display: inline-block;
    padding: 4px 10px;
    border: 1px solid;
    border-radius: 4px;
    line-height: 20px;
  }

  .record-list {
    margin-top: 12px;
  }

  .record-card {
    border: 1px solid #dcdfe6;
    border-radius: 4px;
    background: #fff;
  }

  .record-card + .record-card {
    margin-top: 12px;
  }

  .record-header {
    display: flex;
    min-height: 48px;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 8px 16px;
    background: #fafafa;
    border-bottom: 1px solid #ebeef5;
    box-sizing: border-box;
  }

  .record-identity {
    display: flex;
    min-width: 0;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
  }

  .record-status {
    flex: 0 0 auto;
    padding: 2px 9px;
    font-size: 14px;
    font-weight: 600;
  }

  .record-order {
    min-width: 0;
    color: #303133;
    font-family: monospace;
    font-size: 14px;
    overflow-wrap: anywhere;
  }

  .record-time {
    flex: 0 0 auto;
    color: #909399;
    font-size: 13px;
    font-style: normal;
  }

  .record-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .record-field {
    min-width: 0;
    padding: 12px 16px;
    border-right: 1px solid #ebeef5;
    border-bottom: 1px solid #ebeef5;
    box-sizing: border-box;
  }

  .record-field:nth-child(3n) {
    border-right: 0;
  }

  .record-label {
    margin-bottom: 6px;
    color: #909399;
    font-size: 12px;
    line-height: 18px;
  }

  .record-value {
    color: #303133;
    font-size: 14px;
    line-height: 22px;
    overflow-wrap: anywhere;
    word-break: break-all;
  }

  .code-summary {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 18px;
    color: #606266;
    font-size: 13px;
    font-weight: 400;
  }

  .code-tree-wrap {
    margin-top: 12px;
    overflow-x: auto;
    border: 1px solid #dcdfe6;
    border-radius: 4px;
  }

  .code-tree {
    min-width: 760px;
    padding: 8px 0;

    /deep/ .el-tree-node__content {
      min-height: 44px;
      height: auto;
      padding-right: 12px;
      border-bottom: 1px solid #f0f2f5;
    }

    /deep/ .el-tree-node__content:hover {
      background: #f5f9ff;
    }

    /deep/ .el-tree-node.is-current > .el-tree-node__content {
      background: #fff1f0;
      box-shadow: inset 3px 0 0 #f56c6c;
    }
  }

  .code-node {
    display: flex;
    flex: 1;
    min-width: 0;
    align-items: center;
    justify-content: space-between;
    gap: 20px;
    padding: 7px 0;
  }

  .code-node-main {
    display: flex;
    min-width: 0;
    align-items: center;
    gap: 8px;
  }

  .code-node-label {
    flex: 0 0 auto;
    color: #4887e7;
    font-size: 13px;
  }

  .code-node-value {
    min-width: 0;
    color: #303133;
    font-family: monospace;
    font-size: 14px;
    line-height: 20px;
    overflow-wrap: anywhere;
    user-select: text;
    word-break: break-all;
  }

  .code-node-meta {
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: 12px;
    color: #909399;
    font-size: 12px;
  }

  .code-node-target .code-node-label,
  .code-node-target .code-node-value {
    color: #f56c6c;
    font-weight: 600;
  }

  .sales-icon {
    display: block;
    width: 22px;
    height: 22px;
  }

  .stack-action {
    padding: 0;
  }

  .empty-state {
    min-height: 180px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #909399;
    font-size: 14px;
  }
}

@media (max-width: 1000px) {
  .trace-detail-new {
    .info-grid {
      grid-template-columns: 1fr;
    }

    .record-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .record-field:nth-child(3n) {
      border-right: 1px solid #ebeef5;
    }

    .record-field:nth-child(2n) {
      border-right: 0;
    }
  }
}

@media (max-width: 700px) {
  .trace-detail-new {
    .detail-header,
    .record-header,
    .code-node {
      align-items: flex-start;
      flex-direction: column;
    }

    .detail-header {
      padding-right: 12px;
    }

    .detail-body {
      margin-right: 12px;
      margin-left: 12px;
    }

    .detail-tabs /deep/ .el-tabs__item {
      min-width: 96px;
      padding: 0 10px;
    }

    .info-item {
      grid-template-columns: 96px minmax(0, 1fr);
    }

    .record-grid {
      grid-template-columns: 1fr;
    }

    .record-field,
    .record-field:nth-child(2n),
    .record-field:nth-child(3n) {
      border-right: 0;
    }

    .code-node-meta {
      justify-content: flex-start;
    }
  }
}
</style>
